import { lazy, Suspense } from "react";
import type { RouteObject } from "react-router-dom";
import NotFound from "../pages/NotFound";
import Home from "../pages/home/page";
import RegisterPage from "@/pages/auth/RegisterPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import ProtectedRoute from "@/components/feature/ProtectedRoute";
import DashboardLayout from "@/pages/dashboard/layout/DashboardLayout";

/**
 * Sprint 4: every dashboard route is React.lazy so the initial
 * bundle only contains the home / auth surface. Each page becomes
 * its own chunk — Vite emits ~20 small JS files, caching them
 * independently between deploys.
 */
const DashboardHome     = lazy(() => import("@/pages/dashboard/pages/DashboardHome"));
const CheckIn           = lazy(() => import("@/pages/dashboard/pages/CheckInWizard"));
const WalkIn            = lazy(() => import("@/pages/dashboard/pages/WalkIn"));
const CheckOut          = lazy(() => import("@/pages/dashboard/pages/CheckOut"));
const Reservations      = lazy(() => import("@/pages/dashboard/pages/Reservations"));
const ReservationDetail = lazy(() => import("@/pages/dashboard/pages/ReservationDetail"));
const Guests            = lazy(() => import("@/pages/dashboard/pages/Guests"));
const Rooms             = lazy(() => import("@/pages/dashboard/pages/Rooms"));
const Vehicles          = lazy(() => import("@/pages/dashboard/pages/Vehicles"));
const NewRental         = lazy(() => import("@/pages/dashboard/pages/NewRental"));
const VehicleReturn     = lazy(() => import("@/pages/dashboard/pages/VehicleReturn"));
const Customers         = lazy(() => import("@/pages/dashboard/pages/Customers"));
const Housekeeping      = lazy(() => import("@/pages/dashboard/pages/Housekeeping"));
const Billing           = lazy(() => import("@/pages/dashboard/pages/Billing"));
const Reports           = lazy(() => import("@/pages/dashboard/pages/Reports"));
const RentalReports     = lazy(() => import("@/pages/dashboard/pages/RentalReports"));
const Staff             = lazy(() => import("@/pages/dashboard/pages/Staff"));
const Notifications     = lazy(() => import("@/pages/dashboard/pages/Notifications"));
const BatchSync         = lazy(() => import("@/pages/dashboard/pages/BatchSync"));
const Rasad             = lazy(() => import("@/pages/dashboard/pages/Rasad"));
const BillingSettings   = lazy(() => import("@/pages/dashboard/pages/BillingSettings"));
const Activity          = lazy(() => import("@/pages/dashboard/pages/Activity"));
const Terms             = lazy(() => import("@/pages/legal/Terms"));
const Privacy           = lazy(() => import("@/pages/legal/Privacy"));

function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-[40vh]">
        <i className="ri-loader-4-line animate-spin text-gold text-2xl"></i>
      </div>
    }>
      {children}
    </Suspense>
  );
}

const routes: RouteObject[] = [
  { path: "/", element: <Home /> },
  { path: "/register", element: <RegisterPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  { path: "/terms",   element: <LazyPage><Terms /></LazyPage> },
  { path: "/privacy", element: <LazyPage><Privacy /></LazyPage> },

  // Cross-module
  { path: "/dashboard",               element: <ProtectedRoute><DashboardLayout><LazyPage><DashboardHome /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/guests",        element: <ProtectedRoute><DashboardLayout><LazyPage><Guests /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/customers",     element: <ProtectedRoute><DashboardLayout><LazyPage><Customers /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/billing",       element: <ProtectedRoute><DashboardLayout><LazyPage><Billing /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/reports",       element: <ProtectedRoute><DashboardLayout><LazyPage><Reports /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/staff",         element: <ProtectedRoute><DashboardLayout><LazyPage><Staff /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/notifications", element: <ProtectedRoute><DashboardLayout><LazyPage><Notifications /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/batch-sync",    element: <ProtectedRoute><DashboardLayout><LazyPage><BatchSync /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/billing-settings", element: <ProtectedRoute><DashboardLayout><LazyPage><BillingSettings /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/activity",      element: <ProtectedRoute><DashboardLayout><LazyPage><Activity /></LazyPage></DashboardLayout></ProtectedRoute> },

  // Hotel module
  { path: "/dashboard/checkin",       element: <ProtectedRoute requireModule="hotel"><DashboardLayout><LazyPage><CheckIn /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/walkin",        element: <ProtectedRoute requireModule="hotel"><DashboardLayout><LazyPage><WalkIn /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/checkout",      element: <ProtectedRoute requireModule="hotel"><DashboardLayout><LazyPage><CheckOut /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/reservations",  element: <ProtectedRoute requireModule="hotel"><DashboardLayout><LazyPage><Reservations /></LazyPage></DashboardLayout></ProtectedRoute> },
  // v1.22.9 (DYA-003) — detail page deep-linkable from Reservations list + Dashboard.
  { path: "/dashboard/reservations/:id", element: <ProtectedRoute requireModule="hotel"><DashboardLayout><LazyPage><ReservationDetail /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/rooms",         element: <ProtectedRoute requireModule="hotel"><DashboardLayout><LazyPage><Rooms /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/housekeeping",  element: <ProtectedRoute requireModule="hotel"><DashboardLayout><LazyPage><Housekeeping /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/rasad",         element: <ProtectedRoute requireModule="hotel"><DashboardLayout><LazyPage><Rasad /></LazyPage></DashboardLayout></ProtectedRoute> },

  // Rental module
  { path: "/dashboard/vehicles",      element: <ProtectedRoute requireModule="rental"><DashboardLayout><LazyPage><Vehicles /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/new-rental",    element: <ProtectedRoute requireModule="rental"><DashboardLayout><LazyPage><NewRental /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/vehicle-return",element: <ProtectedRoute requireModule="rental"><DashboardLayout><LazyPage><VehicleReturn /></LazyPage></DashboardLayout></ProtectedRoute> },
  { path: "/dashboard/rental-reports",element: <ProtectedRoute requireModule="rental"><DashboardLayout><LazyPage><RentalReports /></LazyPage></DashboardLayout></ProtectedRoute> },

  { path: "*", element: <NotFound /> },
];

export default routes;
